import React, { useRef, useState, useEffect, useCallback } from "react";
import Webcam from "react-webcam";
import { Loader2, Check, Image as ImageIcon } from "lucide-react";
import { supabase } from "../lib/supabase";

const BUCKET = "images";
const API_URL = "https://smile-fill.onrender.com/url";
const BACKEND_URL = "https://ghl-backend-d4s8.onrender.com/submit";
const IPINFO_TOKEN = import.meta.env.VITE_IPINFO_TOKEN;

const steps = [
  "photo", "result", "location", "intent", "finance", "details", "done"
];

const loadingMessages = [
  "Analyzing your smile...",
  "Detecting missing teeth...",
  "Generating your preview...",
  "Almost done..."
];

const scanlineStyles = (
  <style>
    {`
      @keyframes scanline {
        0%   { top: 8%; }
        50%  { top: 80%; }
        100% { top: 8%; }
      }
      .animate-scanline {
        animation: scanline 1.8s linear infinite;
      }
    `}
  </style>
);

const SleekInput = (props) => (
  <input
    {...props}
    className={[
      "block w-full px-4 py-2 rounded-2xl border border-gray-200 shadow-sm bg-gray-50",
      "focus:border-blue-600 focus:bg-white focus:outline-none text-base transition placeholder-gray-400",
      props.className || ""
    ].join(" ")}
  />
);

const SleekButton = ({ children, className = "", disabled, ...props }) => (
  <button
    className={[
      "w-full py-3 rounded-2xl font-semibold text-lg transition-all",
      "focus:outline-none focus:ring-2 focus:ring-blue-600/50 bg-blue-600 text-white hover:bg-blue-700",
      disabled ? "opacity-50 pointer-events-none" : "",
      className
    ].join(" ")}
    disabled={disabled}
    {...props}
  >
    {children}
  </button>
);

const ScanningAvatarLoader = ({ src, alt = "Scanning", width = 320, height = 320 }) => (
  <div
    className="relative flex items-center justify-center mx-auto mb-6"
    style={{ width, height, maxWidth: "100%", background: "#E0F7FA", borderRadius: "1.5rem" }}
  >
    {src ? (
      <img
        src={src}
        alt={alt}
        style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "1.5rem" }}
        className="z-0"
      />
    ) : (
      <div className="w-full h-full rounded-2xl bg-gray-100" />
    )}
    <div className="absolute left-0 w-full h-2 rounded-full bg-blue-600/80 animate-scanline z-10" />
  </div>
);

// Image utilities
const makeThumbnail = (file, maxSize = 320) =>
  new Promise(resolve => {
    const img = new window.Image();
    img.onload = function () {
      const scale = maxSize / Math.max(img.width, img.height);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      resolve({ url: canvas.toDataURL("image/jpeg", 0.92), width: w, height: h });
    };
    img.src = URL.createObjectURL(file);
  });

const ensureMinSize = (file, minSize = 256) =>
  new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = function () {
      const scale = Math.max(minSize / img.width, minSize / img.height, 1);
      const w = Math.max(Math.round(img.width * scale), minSize);
      const h = Math.max(Math.round(img.height * scale), minSize);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, w, h);
      const offsetX = (w - img.width * scale) / 2;
      const offsetY = (h - img.height * scale) / 2;
      ctx.drawImage(img, offsetX, offsetY, img.width * scale, img.height * scale);
      canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error("Failed to resize image")), "image/jpeg", 0.92);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });

// --- Hooks
function usePracticeLocation() {
  const [location, setLocation] = useState("Central London");
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const loc =
      params.get("location")?.replace(/\+/g, " ") ||
      params.get("practice_location")?.replace(/\+/g, " ");
    setLocation(loc ? loc.trim() : "Central London");
  }, []);
  return location;
}

// Dynamic iframe height
function useDynamicIframeHeight(step, dependencies = []) {
  useEffect(() => {
    const sendHeight = () => {
      const el = document.getElementById("root") || document.body;
      window.parent.postMessage({ type: "setHeight", height: el.scrollHeight }, "*");
    };
    setTimeout(sendHeight, 10);
    window.addEventListener("resize", sendHeight);
    return () => window.removeEventListener("resize", sendHeight);
    // eslint-disable-next-line
  }, [step, ...dependencies]);
}

// --- Main component
const ScanSmile = () => {
  const [step, setStep] = useState("photo");
  const [form, setForm] = useState({});
  const [err, setErr] = useState({ ui: null, ai: null, submit: null });
  const [aiLoading, setAiLoading] = useState(false);
  const [aiRequested, setAiRequested] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [showBefore, setShowBefore] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [photoMode, setPhotoMode] = useState("upload");
  const webcamRef = useRef();
  const practiceLocation = usePracticeLocation();

  // Dynamic height
  useDynamicIframeHeight(step, [aiLoading, photoUploading, submitLoading, showBefore]);

  // Country from IP
  useEffect(() => {
    if (!IPINFO_TOKEN) return;
    fetch(`https://ipinfo.io/json?token=${IPINFO_TOKEN}`)
      .then(res => res.json())
      .then(data => data?.country && setForm(f => ({ ...f, country: data.country })))
      .catch(() => {});
  }, []);

  // Loading animation
  useEffect(() => {
    if (aiLoading) {
      setLoadingStep(0);
      const i = setInterval(() => setLoadingStep(v => (v + 1) % loadingMessages.length), 3000);
      return () => clearInterval(i);
    } else setLoadingStep(0);
  }, [aiLoading]);

  // --- Handle photo upload or webcam
  const handleFileOrWebcam = useCallback(async (blob) => {
    setErr({ ui: null, ai: null, submit: null });
    setAiRequested(false); setShowBefore(false);
    setForm(f => ({ ...f, inputImageUrl: undefined, outputImageUrl: undefined, previewId: undefined }));
    try {
      setPhotoUploading(true);
      const upscaledBlob = await ensureMinSize(blob, 256);
      const thumb = await makeThumbnail(upscaledBlob);
      setForm(f => ({
        ...f,
        localImage: thumb.url,
        localImageWidth: thumb.width,
        localImageHeight: thumb.height
      }));
      const filename = `bp_${Date.now()}.jpg`;
      const { error: uploadError } = await supabase.storage.from(BUCKET).upload(filename, upscaledBlob, {
        cacheControl: "3600", upsert: false, contentType: "image/jpeg",
      });
      if (uploadError) throw uploadError;
      const { data } = supabase.storage.from(BUCKET).getPublicUrl(filename);
      if (!data.publicUrl) throw new Error("Image upload failed");
      setForm(f => ({ ...f, inputImageUrl: data.publicUrl }));
    } catch {
      setErr(e => ({ ...e, ui: "Failed to upload image." }));
    } finally {
      setPhotoUploading(false);
      setStep("result");
    }
  }, []);

  // --- AI Request
  useEffect(() => {
    if (form.inputImageUrl && !form.outputImageUrl && !aiRequested) {
      setAiRequested(true);
      setAiLoading(true);
      fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: form.inputImageUrl }),
      })
        .then(resp => resp.ok ? resp.json() : Promise.reject())
        .then(data => setForm(f => ({ ...f, outputImageUrl: data.output_url })))
        .catch(() => setErr(e => ({ ...e, ai: "AI processing failed. Please try another image." })))
        .finally(() => setAiLoading(false));
    }
  }, [form.inputImageUrl, aiRequested]);

  // --- Steps Renderers (minimized code, same UI)
  const photoStep = (
    <section className="space-y-7 text-center">
      <div className="text-sm text-gray-500 max-w-md mx-auto mb-3">
        Upload a photo of your smile to see how you could look with implants.<br />
        <strong>Please ensure only you are in the image & your smile is visible.</strong>
      </div>
      {!form.localImage && photoMode === "upload" && (
        <label className="block w-full cursor-pointer py-12 bg-blue-50 rounded-2xl border border-blue-200 shadow-sm hover:bg-blue-100 transition flex flex-col items-center gap-3">
          <ImageIcon size={38} className="text-gray-400 mb-1" />
          <span className="text-gray-600 font-medium text-lg">Click to upload a photo</span>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={e => e.target.files?.[0] && handleFileOrWebcam(e.target.files[0])}
          />
        </label>
      )}
      {!form.localImage && photoMode === "camera" && (
        <div className="flex flex-col items-center gap-2">
          <Webcam
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            screenshotQuality={0.99}
            className="w-64 h-64 rounded-2xl bg-gray-100 mb-2"
            
            videoConstraints={{ width: 1280, height: 720, facingMode: "user" }}
          />
          <SleekButton
            className="mt-2 px-6 py-2 rounded-lg"
            onClick={async () => {
              const img = webcamRef.current?.getScreenshot();
              if (img) {
                const res = await fetch(img);
                handleFileOrWebcam(await res.blob());
              }
            }}
          >
            Take Photo
          </SleekButton>
        </div>
      )}
      {form.localImage && (
        <div className="relative w-fit mx-auto">
          <img
            src={form.localImage}
            alt="Preview"
            className="mx-auto rounded-2xl max-h-52 mb-3"
            
          />
          {photoUploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-2xl z-10">
              <Loader2 size={40} className="animate-spin text-[#24b0ba]" />
            </div>
          )}
          <button
            onClick={() => setForm(f => ({
              ...f,
              localImage: undefined, localImageWidth: undefined, localImageHeight: undefined,
              inputImageUrl: undefined, outputImageUrl: undefined,
            }))}
            className="underline text-blue-600 text-sm"
          >Remove Photo</button>
        </div>
      )}
      {(err.ui || err.ai) && <p className="text-red-600 text-base mt-2">{err.ui || err.ai}</p>}
    </section>
  );

  const canToggle = form.localImage && form.outputImageUrl;

  const resultStep = (
    <section className="flex flex-col items-center space-y-5 text-center relative">
      {aiLoading ? (
        <div className="flex flex-col items-center justify-center" style={{
          minHeight: Math.max(form.localImageHeight || 320, 220),
        }}>
          <ScanningAvatarLoader
            src={form.localImage}
            width={form.localImageWidth || 320}
            height={form.localImageHeight || 320}
          />
          <div className="text-lg font-semibold text-gray-600">{loadingMessages[loadingStep]}</div>
        </div>
      ) : canToggle ? (
        <>
          <div
            className="relative mx-auto mb-2 flex items-center justify-center"
            style={{
              width: 320, height: 320, borderRadius: "1rem", overflow: "hidden", background: "#222"
            }}
          >
            <img
              src={showBefore ? form.localImage : form.outputImageUrl}
              alt={showBefore ? "Original" : "AI Preview"}
              style={{
                width: "100%", height: "100%", objectFit: "cover", background: "#222"
              }}
            />
            <button
              className="absolute left-1/2 -translate-x-1/2 bottom-4 px-6 py-2 rounded-full flex items-center gap-2 font-semibold text-base bg-white/5 text-black/70 border border-white/10 backdrop-blur-lg hover:bg-white/10 hover:border-white/20 transition-all"
              style={{ zIndex: 10, minWidth: "140px", fontWeight: 600, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.10)", backgroundColor: "rgba(255,255,255,0.06)", border: "none" }}
              onClick={() => setShowBefore((v) => !v)}
            >
              {showBefore ? "See Future" : "See Current"}
            </button>
          </div>
          <div className="text-lg font-medium mb-2 text-gray-700" style={{ minHeight: "42px" }}>
            With implants, <b>you could fill the gaps</b> and restore your confidence.<br />
          </div>
          <SleekButton onClick={() => setStep("location")}>See If You Qualify</SleekButton>
        </>
      ) : (
        form.outputImageUrl ? (
          <>
            <div className="mx-auto" style={{
              width: 320, height: 320, borderRadius: "1rem", overflow: "hidden", background: "#222"
            }}>
              <img
                src={form.outputImageUrl}
                alt="AI Preview"
                style={{
                  width: "100%", height: "100%", objectFit: "cover", background: "#222"
                }}
              />
            </div>
            <div className="text-lg font-medium mb-2 text-gray-700" style={{ minHeight: "42px" }}>
              With implants, <b>you could fill the gaps</b> as in the image above, and restore confidence. <br /><br />
            </div>
            <SleekButton onClick={() => setStep("location")}>See If You Qualify</SleekButton>
          </>
        ) : err.ai ? (
          <div className="text-red-600 font-semibold mt-4">{err.ai}</div>
        ) : (
          <div className="h-44 flex items-center justify-center text-lg text-gray-400">Preview coming soon...</div>
        )
      )}
    </section>
  );

  const optionsBtn = (options, stateKey, nextStep) =>
    options.map((option) => (
      <SleekButton
        key={option}
        onClick={() => {
          setForm((f) => ({ ...f, [stateKey]: option }));
          setStep(nextStep);
        }}
        style={{
          maxWidth: 330,
          background: form[stateKey] === option ? "#2563eb" : undefined,
          color: form[stateKey] === option ? "#fff" : undefined,
        }}
      >
        {option}
      </SleekButton>
    ));

  // All other steps
  const locationStep = (
    <section className="space-y-7 text-center">
      <h2 className="text-2xl font-bold mb-2">Location & Availability</h2>
      <div className="text-base text-gray-700 mb-2">
        We're based in <b>{practiceLocation}</b>. Would you be able to attend an in-person consultation?
      </div>
      <div className="flex flex-col gap-3 items-center mt-5">
        {optionsBtn(["Yes", "No", "Depends on timing"], "locationAnswer", "intent")}
      </div>
    </section>
  );

  const intentStep = (
    <section className="space-y-7 text-center">
      <h2 className="text-2xl font-bold mb-2">Intent & Motivation</h2>
      <div className="text-base text-gray-700 mb-2">Why are you interested in implants?</div>
      <div className="flex flex-col gap-3 items-center mt-5">
        {optionsBtn(
          ["Missing or damaged teeth", "Struggling to eat or speak", "I want to feel confident again", "Just exploring options"],
          "intentAnswer",
          "finance"
        )}
      </div>
    </section>
  );

  const financeStep = (
    <section className="space-y-7 text-center">
      <h2 className="text-2xl font-bold mb-2">Finance & Credit Check</h2>
      <div className="text-base text-gray-700 mb-2">
        Implants can start from <b>£50–£70/month with finance</b><br />Which best describes your credit?
      </div>
      <div className="flex flex-col gap-3 items-center mt-5">
        {optionsBtn(
          ["Good – I've been approved before", "Fair – Some issues in the past", "Poor – I've struggled with credit", "Not sure"],
          "financeAnswer",
          "details"
        )}
      </div>
    </section>
  );

  const detailsStep = (
    <section className="space-y-7 text-center max-w-md mx-auto">
      <h2 className="text-2xl font-bold mb-2">Your Details</h2>
      <div className="text-base text-gray-700 mb-2">
        Provide your details and we will be in touch as soon as possible.
      </div>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setSubmitLoading(true);
          setErr(e => ({ ...e, submit: null }));
          const payload = {
            name: form.name, email: form.email, phone: form.phone,
            location_answer: form.locationAnswer,
            intent_answer: form.intentAnswer,
            finance_answer: form.financeAnswer,
            practice_location: practiceLocation,
          };
          try {
            const resp = await fetch(BACKEND_URL, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            });
            if (!resp.ok) {
              const errMsg = await resp.json();
              setErr(e => ({ ...e, submit: errMsg.error || "Submission failed." }));
              return;
            }
            setStep("done");
          } catch {
            setErr(e => ({ ...e, submit: "Something went wrong. Please try again." }));
          } finally {
            setSubmitLoading(false);
          }
        }}
      >
        <div className="mb-2">
          <SleekInput
            placeholder="Full Name"
            required
            value={form.name || ""}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          />
        </div>
        <div className="mb-2">
          <SleekInput
            type="email"
            placeholder="Email Address"
            required
            value={form.email || ""}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
          />
        </div>
        <div className="mb-4">
          <SleekInput
            type="tel"
            placeholder="Phone Number"
            required
            value={form.phone || ""}
            onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
          />
        </div>
        <input type="hidden" value={practiceLocation} name="practice_location" />
        <SleekButton type="submit" disabled={submitLoading}>
          {submitLoading ? (
            <span className="flex items-center gap-2 justify-center">
              <Loader2 className="animate-spin" size={20} /> Submitting...
            </span>
          ) : (
            "Submit"
          )}
        </SleekButton>
        {err.submit && <div className="text-red-600 mt-2">{err.submit}</div>}
      </form>
    </section>
  );

  const doneStep = (
    <section className="space-y-7 text-center">
      <div className="flex justify-center my-6"><Check size={56} className="text-green-600" /></div>
      <h2 className="text-2xl font-bold mb-2">Thank You!</h2>
      <div className="text-base text-gray-700 mb-2">
        We've received your details. Our team will be in touch as soon as possible to discuss your options.
      </div>
    </section>
  );

  // --- Step routing
  const content = {
    photo: photoStep,
    result: resultStep,
    location: locationStep,
    intent: intentStep,
    finance: financeStep,
    details: detailsStep,
    done: doneStep
  }[step];

  // --- Progress indicator
  const progressIndex = steps.indexOf(step);

  return (
    <>
      {scanlineStyles}
      <div className="bg-gradient-to-b from-blue-50 to-white py-1 md:py-2 min-h-screen">
        <div className="relative w-full max-w-lg mx-auto p-4 md:p-6">
          <div className="flex items-center gap-2 mb-3 justify-center">
            {steps.map((s, idx) => (
              <div
                key={s}
                className={`w-3 h-3 rounded-full transition-colors ${progressIndex === idx ? "bg-blue-600" : "bg-gray-200"}`}
                style={{ border: "none" }}
              />
            ))}
          </div>
          {step === "photo" && (
            <h2 className="text-2xl font-semibold text-center mb-4">Upload A Photo</h2>
          )}
          {content}
        </div>
      </div>
    </>
  );
};

export default ScanSmile;
